import { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PING_URL = `${BACKEND_URL}/api/`;
const FAST_TIMEOUT_MS = 2500;
const RETRY_TIMEOUT_MS = 8000;
const RETRY_INTERVAL_MS = 2500;

/**
 * Hook that pings the API on mount and surfaces a friendly "waking up" state
 * if the server is idle/cold. Auto-retries until the API responds.
 *
 * Returns one of:
 *   { status: 'checking' }  - initial fast ping (0-2.5s, no UI yet)
 *   { status: 'waking' }    - first ping slow/failed; auto-retrying
 *   { status: 'ready' }     - API responded; safe to render the page
 */
export const useApiWakeUp = () => {
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;

    const fastPing = async () => {
      try {
        await axios.get(PING_URL, { timeout: FAST_TIMEOUT_MS });
        if (!cancelled) setStatus('ready');
      } catch {
        if (cancelled) return;
        setStatus('waking');
        scheduleRetry();
      }
    };

    const slowPing = async () => {
      try {
        await axios.get(PING_URL, { timeout: RETRY_TIMEOUT_MS });
        if (!cancelled) setStatus('ready');
      } catch {
        if (!cancelled) scheduleRetry();
      }
    };

    const scheduleRetry = () => {
      retryTimer = setTimeout(slowPing, RETRY_INTERVAL_MS);
    };

    fastPing();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, []);

  return status;
};
